import React, { forwardRef, useImperativeHandle } from 'react';
import { detectVideoSource } from './utils';
import { VideoPlayerProps, VideoPlayerMethods } from './types';
import YouTubePlayer from './YouTubePlayer';
import StorageVideoPlayer from './StorageVideoPlayer';

/**
 * Unified Video Player Component
 * Detects video source type and renders the appropriate player component
 */
const VideoPlayer = forwardRef<VideoPlayerMethods, VideoPlayerProps>(({
  videoUrl,
  videoSourceType,
  ...props
}, ref) => {
  // Detect video source if not explicitly provided
  const sourceType = videoSourceType || detectVideoSource(videoUrl);

  // Create refs for different player types
  const youtubePlayerRef = React.useRef<VideoPlayerMethods | null>(null);
  const storagePlayerRef = React.useRef<VideoPlayerMethods | null>(null);

  // Determine which player ref to use based on source type
  const getActivePlayerRef = () => {
    switch (sourceType) {
      case 'youtube':
        return youtubePlayerRef;
      case 'supabase_storage':
      case 'cloudflare_r2':
      case 'local':
        return storagePlayerRef;
      default:
        return null;
    }
  };

  // Expose player methods through ref
  useImperativeHandle(ref, () => {
    const activeRef = getActivePlayerRef();
    return {
      play: () => activeRef?.current?.play(),
      pause: () => activeRef?.current?.pause(),
      seekTo: (seconds: number) => activeRef?.current?.seekTo(seconds),
      getCurrentTime: () => activeRef?.current?.getCurrentTime() || 0,
      getDuration: () => activeRef?.current?.getDuration() || 0,
      setVolume: (volume: number) => activeRef?.current?.setVolume(volume),
      getVolume: () => activeRef?.current?.getVolume() || 0,
      setPlaybackRate: (rate: number) => activeRef?.current?.setPlaybackRate(rate),
      getPlaybackRate: () => activeRef?.current?.getPlaybackRate() || 1,
      mute: () => activeRef?.current?.mute(),
      unMute: () => activeRef?.current?.unMute(),
      isMuted: () => activeRef?.current?.isMuted() || false,
    };
  });

  // Route to appropriate player based on source type
  switch (sourceType) {
    case 'youtube':
      return <YouTubePlayer ref={youtubePlayerRef} videoUrl={videoUrl} {...props} />;

    case 'supabase_storage':
    case 'cloudflare_r2':
    case 'local':
      return <StorageVideoPlayer ref={storagePlayerRef} videoUrl={videoUrl} {...props} />;

    case 'unknown':
    default:
      return (
        <div className="flex items-center justify-center bg-gray-100 p-8">
          <p className="text-gray-500 text-sm">
            Unsupported video source. Please provide a YouTube URL.
          </p>
        </div>
      );
  }
});

VideoPlayer.displayName = 'VideoPlayer';

export default VideoPlayer;

